import React from "react";
import { Link } from "react-router-dom";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * BrandedHero — CP-inspired gradient hero banner used on the top of key
 * V2 pages (Dashboard, WO Detail, Property Detail). Uses the shared design
 * theme so brand color changes propagate everywhere.
 *
 * Props:
 *  - breadcrumbs: [{ to?, label }]
 *  - eyebrow: small text above title (like status chips)
 *  - title: main text (or ReactNode)
 *  - subtitle: small helper text below title
 *  - actions: right-aligned ReactNode
 *  - chips: array of { label, icon? } shown below title
 *  - size: "sm" | "md" | "lg" — controls padding/overlap depth
 *  - children: floating content that overlaps the hero bottom (e.g. stat cards)
 */
export default function BrandedHero({
  breadcrumbs,
  eyebrow,
  title,
  subtitle,
  actions,
  chips,
  size = "md",
  className = "",
}) {
  const { theme } = useDesignTheme();

  const pads = {
    sm: "pt-5 pb-14 sm:pt-6 sm:pb-16",
    md: "pt-5 pb-20 sm:pt-8 sm:pb-24",
    lg: "pt-6 pb-28 sm:pt-8 sm:pb-32 lg:pt-10 lg:pb-40",
  };

  return (
    <div
      data-testid="branded-hero"
      className={`${theme.gradient} ${pads[size] || pads.md} px-4 sm:px-6 lg:px-8 w-full rounded-b-3xl relative ${className}`}
    >
      <div className="max-w-[1600px] mx-auto">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest">
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={i}>
                {b.to ? (
                  <Link to={b.to} className="hover:text-white">{b.label}</Link>
                ) : (
                  <span>{b.label}</span>
                )}
                {i < breadcrumbs.length - 1 && <span>·</span>}
              </React.Fragment>
            ))}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-white/80 text-[11px] font-black uppercase tracking-widest">{eyebrow}</div>
            )}
            <h1 className="text-white font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="branded-hero-title">
              {title}
            </h1>
            {subtitle && (
              <div className="text-white/80 text-xs sm:text-sm font-medium mt-1 max-w-2xl">{subtitle}</div>
            )}
            {chips && chips.length > 0 && (
              <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                {chips.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest">
                    {c.icon && <span>{c.icon}</span>}
                    {c.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          {actions && <div className="flex items-center gap-1.5 flex-wrap">{actions}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * FloatingCardsArea — companion wrapper that sits below the hero and pulls
 * up into the gradient with a negative margin. Use for stat cards etc.
 */
export function FloatingCardsArea({ children, className = "" }) {
  return (
    <div className={`max-w-[1600px] mx-auto -mt-16 sm:-mt-20 relative z-10 ${className}`}>
      {children}
    </div>
  );
}
